import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import type { ConteggioSessione } from '@eventi/assegna.ts';
import { avanzamento, conScelta, sceltaDelTurno, scelteDisponibili, senzaScelta, sessionePerCodice } from '@eventi/regole.ts';
import type { Percorso, ProgrammaEvento, RegoleScelta, SessioneEvento } from '@eventi/tipi.ts';
import { nomeAula } from '../../lib/eventi/programma';
import { dataOraIt } from '../../lib/eventi/stati';
import type { PartecipanteEvento } from '../../lib/eventi/tipi';

const NESSUNA = '__nessuna__';

type Props = {
  /** Chi si modifica. Il genitore la apre con key={id}: ogni persona parte pulita. */
  partecipante: PartecipanteEvento;
  programma: ProgrammaEvento;
  regole: RegoleScelta;
  conteggi: Map<string, ConteggioSessione>;
  onChiudi: () => void;
  onSalva: (percorso: Percorso) => Promise<void>;
};

function etichettaOpzione(programma: ProgrammaEvento, s: SessioneEvento, c: ConteggioSessione | undefined): string {
  const posti = c?.posti ?? null;
  const occupazione = posti === null ? `${c?.assegnati ?? 0} assegnati` : `${c?.assegnati ?? 0}/${posti}`;
  const piena = posti !== null && (c?.assegnati ?? 0) >= posti ? ' · piena' : '';
  return `${nomeAula(programma, s.aula)} · ${s.titolo} (${occupazione}${piena})`;
}

/**
 * Cambia a mano il percorso assegnato a una persona, turno per turno. I menu
 * seguono le regole dell'evento (una lezione ripetuta sparisce dagli altri
 * turni dello stesso giorno). Accanto a ogni turno c'e' cosa aveva chiesto.
 */
export function ModificaPercorsoDialog({ partecipante, programma, regole, conteggi, onChiudi, onSalva }: Props) {
  // Se non ha ancora un'assegnazione si parte da quello che ha chiesto.
  const [percorso, setPercorso] = useState<Percorso>(
    () => (partecipante.assegnazione_stato !== 'nessuna' ? partecipante.assegnazione : partecipante.richiesta) ?? {},
  );
  const [salvando, setSalvando] = useState(false);

  const perCodice = sessionePerCodice(programma);
  const { fatti, totale } = avanzamento(programma, percorso);
  const confermato = partecipante.assegnazione_stato === 'confermata';

  const scegli = (giorno: string, turno: number, valore: string) => {
    const s = perCodice.get(valore);
    setPercorso((prima) => (valore === NESSUNA || !s ? senzaScelta(programma, prima, giorno, turno) : conScelta(programma, prima, s)));
  };

  const salva = async () => {
    setSalvando(true);
    try {
      await onSalva(percorso);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{partecipante.nome}</DialogTitle>
          <DialogDescription>
            {partecipante.email} · {fatti} turni assegnati su {totale}. Tra parentesi: assegnati / posti dell'aula.
          </DialogDescription>
        </DialogHeader>

        {confermato && (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              Ha gia' ricevuto il percorso il {dataOraIt(partecipante.confermata_at)}. Se salvi una modifica
              l'assegnazione torna in bozza: finche' non la riconfermi non la vede nell'app, e quando la riconfermi
              riceve una nuova email.
            </p>
          </div>
        )}

        <div className="space-y-4 py-1">
          {programma.giorni.map((g) => (
            <section key={g.codice}>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{g.nome}</h3>
              <div className="space-y-3">
                {programma.turni.map((t) => {
                  const attuale = percorso[g.codice]?.[String(t.n)] ?? null;
                  const opzioni = scelteDisponibili(programma, regole, percorso, g.codice, t.n);
                  const fuoriMenu = attuale && !opzioni.some((o) => o.codice === attuale);
                  const chiesta = sceltaDelTurno(programma, partecipante.richiesta, g.codice, t.n);
                  return (
                    <div key={t.n} className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center">
                      <span className="text-xs text-neutral-600">
                        Turno {t.n} <span className="text-neutral-400">{t.inizio}–{t.fine}</span>
                      </span>
                      <div className="min-w-0">
                        <Select value={attuale ?? NESSUNA} onValueChange={(v) => scegli(g.codice, t.n, v)}>
                          <SelectTrigger className="h-9 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NESSUNA}>— Nessuna aula —</SelectItem>
                            {fuoriMenu && <SelectItem value={attuale}>{attuale} (non piu' disponibile)</SelectItem>}
                            {opzioni.map((s) => (
                              <SelectItem key={s.codice} value={s.codice}>
                                {etichettaOpzione(programma, s, conteggi.get(s.codice))}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-neutral-500 mt-0.5 truncate">
                          Ha chiesto: {chiesta ? `${nomeAula(programma, chiesta.aula)} · ${chiesta.titolo}` : '—'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {regole.tutti_i_turni && fatti < totale && (
          <p className="text-xs text-amber-700">Mancano {totale - fatti} turni: puoi salvare lo stesso e completare dopo.</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} disabled={salvando}>
            Annulla
          </Button>
          <Button onClick={salva} disabled={salvando} className="bg-primary-600 hover:bg-primary-700 text-white">
            {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salva in bozza
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
